/**
 * Swamithra — the Vetting tab (US-01-004).
 *
 * OPENING IT IS A READ. `GET /expert/vetting-requests/{vettingRef}` changes
 * nothing; the status stays Open until an Expert posts the first message,
 * which moves it to In Progress and records the reviewer (the same shape as a
 * query). There is no exclusivity: any Expert may open it and post.
 *
 * The list offers a status filter and NOTHING ELSE — no search, no category
 * filter, no date range, no SLA column (US-01-004.3). That absence is specified,
 * so there is no parameter here to express them.
 */

import { type QueryCategory } from "../domain/categories"
import { referenceParam } from "../domain/ids"
import { type PageParams, pageQuery, paginated } from "../domain/pagination"
import { expertCategoryUpdateSchema } from "../domain/query"
import { type SendFromDraft } from "../domain/response-draft"
import {
	expertVettingDetailSchema,
	type ExpertVettingFilters,
	expertVettingRowSchema,
	type VettingMessageInput,
	vettingMessageInputSchema,
	vettingMessageSchema,
} from "../domain/vetting"
import { appendFiles, type PlatformFile } from "../files"
import { api } from "./client"
import { type ReadOptions, type SubmitOptions } from "./options"
import { appendDraftSend } from "./panel-drafts"

const expertVettingPage = paginated(expertVettingRowSchema)

/** Every request regardless of status, oldest submission first by default. */
export function fetchExpertVettingRequests(
	filters: ExpertVettingFilters = {},
	page: PageParams = {},
	options: ReadOptions = {},
) {
	return api.get("/expert/vetting-requests", expertVettingPage, {
		...options,
		// STATUS IS THE ONLY FILTER, and the only parameter beside the page: the
		// Vetting tab offers no search, no category filter, no date range and no
		// sort control (US-01-004.3), and the service orders the list oldest
		// submission first for exactly that reason (US-01-004.1).
		query: {
			...pageQuery(page),
			...(filters.status ? { status: filters.status } : {}),
		},
	})
}

/** One request as an Expert reviews it. A read: the status does not move. */
export function fetchExpertVettingRequest(vettingRef: string, options: ReadOptions = {}) {
	return api.get(
		`/expert/vetting-requests/${referenceParam(vettingRef)}`,
		expertVettingDetailSchema,
		options,
	)
}

/**
 * Posts an Expert message into the thread.
 *
 * A second and later message is never refused — unlike the old single-review
 * rule, the thread simply grows and any Expert may add to it (the panel is a
 * shared pool). Mirrors postExpertResponse for queries.
 */
export async function postExpertVettingMessage(
	vettingRef: string,
	input: VettingMessageInput,
	attachments: readonly PlatformFile[],
	options: SubmitOptions,
	fromDraft?: SendFromDraft,
) {
	const parsed = vettingMessageInputSchema.parse(input)
	const form = new FormData()
	form.append("body_rich", parsed.bodyRich)
	appendDraftSend(form, fromDraft)
	appendFiles(form, "attachments", attachments)
	return await api.post(
		`/expert/vetting-requests/${referenceParam(vettingRef)}/messages`,
		vettingMessageSchema,
		form,
		{ signal: options.signal, idempotencyKey: options.idempotencyKey },
	)
}

/**
 * Marks a vetting request as Answered and notifies the user.
 *
 * This is called after the expert has submitted their review and decides to
 * mark it as answered. A WhatsApp notification is sent with a link to the review.
 */
export async function markVettingAsAnswered(vettingRef: string, options: SubmitOptions) {
	return await api.post(
		`/expert/vetting-requests/${referenceParam(vettingRef)}/mark-answered`,
		expertVettingRowSchema,
		{},
		{ signal: options.signal, idempotencyKey: options.idempotencyKey },
	)
}

/**
 * Closes an Answered vetting request at an Expert's request, as `closeQuery`
 * does for a query. Anything but Answered is a 409; no WhatsApp message is
 * sent for a close.
 */
export function closeVettingRequest(vettingRef: string, options: SubmitOptions) {
	return api.post(
		`/expert/vetting-requests/${referenceParam(vettingRef)}/close`,
		expertVettingRowSchema,
		undefined,
		{ signal: options.signal, idempotencyKey: options.idempotencyKey },
	)
}

/** Corrects a vetting request category and leaves the audit trail to the server. */
export async function updateVettingCategory(
	vettingRef: string,
	category: QueryCategory,
	options: ReadOptions = {},
) {
	return await api.patch(
		`/expert/vetting-requests/${referenceParam(vettingRef)}`,
		expertVettingRowSchema,
		expertCategoryUpdateSchema.parse({ category }),
		options,
	)
}
